import { useState } from "react";
import { useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X, ChevronDown } from "lucide-react";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import logo from "@/assets/logo-color.png";
import ScrollLink from "@/components/ScrollLink";

// Mobile Accordion Component for collapsible submenus
const MobileAccordion = ({ title, children }: { title: string; children: React.ReactNode }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div className="px-4 py-2">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full text-sm font-medium text-muted-foreground hover:text-primary"
        aria-expanded={isOpen}
      >
        {title}
        <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && (
        <div className="mt-2 flex flex-col gap-1">
          {children}
        </div>
      )}
    </div>
  );
};

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const isActive = (path: string) => location.pathname === path;

  const hostingLinks = [
    { title: "BDIX Web Hosting", href: "/bdix-web-hosting", description: "High-performance BDIX optimized hosting in Bangladesh" },
    { title: "BDIX Turbo Hosting", href: "/bdix-turbo-hosting", description: "Lightning-fast turbo-charged BDIX hosting" },
  ];

  const vpsLinks = [
    { title: "BDIX VPS", href: "/bdix-vps", description: "Full root access BDIX VPS for experts" },
    { title: "BDIX Managed VPS", href: "/bdix-managed-vps", description: "Fully managed BDIX VPS with 24/7 support" },
  ];

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border/40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container-custom flex h-16 items-center justify-between">
        {/* Logo - Image only, easy to control width */}
        <ScrollLink to="/" className="flex items-center">
          <img 
            src={logo} 
            alt="Web Host Buy BD - Best BDIX Hosting Provider in Bangladesh" 
            className="h-10 w-auto object-contain"
            fetchPriority="high"
            width={180}
            height={40}
            style={{ maxWidth: '180px' }} /* Easy to adjust logo width */
          />
        </ScrollLink>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center gap-1" aria-label="Main navigation">
          <ScrollLink
            to="/"
            className={`px-4 py-2 text-sm font-medium transition-colors hover:text-primary ${
              isActive("/") ? "text-primary" : "text-muted-foreground"
            }`}
          >
            Home
          </ScrollLink>
          
          <ScrollLink
            to="/domain"
            className={`px-4 py-2 text-sm font-medium transition-colors hover:text-primary ${
              isActive("/domain") ? "text-primary" : "text-muted-foreground"
            }`}
          >
            Domain
          </ScrollLink>

          <NavigationMenu>
            <NavigationMenuList>
              <NavigationMenuItem>
                <NavigationMenuTrigger className="text-sm font-medium text-muted-foreground hover:text-primary bg-transparent">
                  Hosting
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <ul className="grid w-[300px] gap-2 p-4">
                    {hostingLinks.map((link) => (
                      <li key={link.href}>
                        <NavigationMenuLink asChild>
                          <ScrollLink
                            to={link.href}
                            className="group block select-none rounded-lg p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                          >
                            <div className="text-sm font-medium leading-none">{link.title}</div>
                            <p className="line-clamp-2 text-sm leading-snug text-muted-foreground group-hover:text-accent-foreground/80 mt-1">
                              {link.description}
                            </p>
                          </ScrollLink>
                        </NavigationMenuLink>
                      </li>
                    ))}
                  </ul>
                </NavigationMenuContent>
              </NavigationMenuItem>

              <NavigationMenuItem>
                <NavigationMenuTrigger className="text-sm font-medium text-muted-foreground hover:text-primary bg-transparent">
                  VPS
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <ul className="grid w-[300px] gap-2 p-4">
                    {vpsLinks.map((link) => (
                      <li key={link.href}>
                        <NavigationMenuLink asChild>
                          <ScrollLink
                            to={link.href}
                            className="group block select-none rounded-lg p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                          >
                            <div className="text-sm font-medium leading-none">{link.title}</div>
                            <p className="line-clamp-2 text-sm leading-snug text-muted-foreground group-hover:text-accent-foreground/80 mt-1">
                              {link.description}
                            </p>
                          </ScrollLink>
                        </NavigationMenuLink>
                      </li>
                    ))}
                  </ul>
                </NavigationMenuContent>
              </NavigationMenuItem>
            </NavigationMenuList>
          </NavigationMenu>

          <ScrollLink
            to="/bdix-dedicated-server"
            className={`px-4 py-2 text-sm font-medium transition-colors hover:text-primary ${
              isActive("/bdix-dedicated-server") ? "text-primary" : "text-muted-foreground"
            }`}
          >
            Dedicated Server
          </ScrollLink>

          <ScrollLink
            to="/bdix-reseller-hosting"
            className={`px-4 py-2 text-sm font-medium transition-colors hover:text-primary ${
              isActive("/bdix-reseller-hosting") ? "text-primary" : "text-muted-foreground"
            }`}
          >
            Reseller
          </ScrollLink>

          <ScrollLink
            to="/about-us"
            className={`px-4 py-2 text-sm font-medium transition-colors hover:text-primary ${
              isActive("/about-us") ? "text-primary" : "text-muted-foreground"
            }`}
          >
            About
          </ScrollLink>

          <ScrollLink
            to="/contact-us"
            className={`px-4 py-2 text-sm font-medium transition-colors hover:text-primary ${
              isActive("/contact-us") ? "text-primary" : "text-muted-foreground"
            }`}
          >
            Contact
          </ScrollLink>
        </nav>

        {/* Client Area Button */}
        <div className="flex items-center gap-4">
          <Button variant="client" size="default" className="hidden sm:flex" asChild>
            <a href="https://my.webhostbuybd.com">Client Area</a>
          </Button>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileMenuOpen}
          >
            {mobileMenuOpen ? <X className="h-6 w-6" aria-hidden="true" /> : <Menu className="h-6 w-6" aria-hidden="true" />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      {mobileMenuOpen && (
        <div className="lg:hidden border-t border-border bg-background">
          <nav className="container-custom py-4 flex flex-col gap-2" aria-label="Mobile navigation">
            <ScrollLink to="/" className="px-4 py-2 text-sm font-medium hover:text-primary" onClick={() => setMobileMenuOpen(false)}>
              Home
            </ScrollLink>
            <ScrollLink to="/domain" className="px-4 py-2 text-sm font-medium hover:text-primary" onClick={() => setMobileMenuOpen(false)}>
              Domain
            </ScrollLink>
            {/* Hosting Accordion */}
            <MobileAccordion title="Hosting">
              <ScrollLink to="/bdix-web-hosting" className="block text-sm hover:text-primary py-2 pl-4" onClick={() => setMobileMenuOpen(false)}>
                BDIX Web Hosting
              </ScrollLink>
              <ScrollLink to="/bdix-turbo-hosting" className="block text-sm hover:text-primary py-2 pl-4" onClick={() => setMobileMenuOpen(false)}>
                BDIX Turbo Hosting
              </ScrollLink>
            </MobileAccordion>
            {/* VPS Accordion */}
            <MobileAccordion title="VPS">
              <ScrollLink to="/bdix-vps" className="block text-sm hover:text-primary py-2 pl-4" onClick={() => setMobileMenuOpen(false)}>
                BDIX VPS
              </ScrollLink>
              <ScrollLink to="/bdix-managed-vps" className="block text-sm hover:text-primary py-2 pl-4" onClick={() => setMobileMenuOpen(false)}>
                BDIX Managed VPS
              </ScrollLink>
            </MobileAccordion>
            <ScrollLink to="/bdix-dedicated-server" className="px-4 py-2 text-sm font-medium hover:text-primary" onClick={() => setMobileMenuOpen(false)}>
              Dedicated Server
            </ScrollLink>
            <ScrollLink to="/bdix-reseller-hosting" className="px-4 py-2 text-sm font-medium hover:text-primary" onClick={() => setMobileMenuOpen(false)}>
              Reseller
            </ScrollLink>
            <ScrollLink to="/about-us" className="px-4 py-2 text-sm font-medium hover:text-primary" onClick={() => setMobileMenuOpen(false)}>
              About
            </ScrollLink>
            <ScrollLink to="/contact-us" className="px-4 py-2 text-sm font-medium hover:text-primary" onClick={() => setMobileMenuOpen(false)}>
              Contact
            </ScrollLink>
            <div className="px-4 py-2">
              <Button variant="client" size="default" className="w-full" asChild>
                <a href="https://my.webhostbuybd.com">Client Area</a>
              </Button>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;